import { Link } from "react-router-dom";
import { Linkedin, Youtube } from "lucide-react";
import { XLogo } from "./icons/XLogo";
import Logo from "./Logo";

const COLUMNS = [
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Leadership", to: "/leadership" },
      { label: "Careers", to: "/careers" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Digital Engineering", to: "/services/web-application-development" },
      { label: "AI & Data", to: "/services/ai-solutions-generative-ai" },
      { label: "Cloud & Architecture", to: "/services/cloud-solution-architecture" },
      { label: "Enterprise Integration", to: "/services/system-integration" },
      { label: "DevOps", to: "/services/devops-cloud-engineering" },
    ],
  },
  {
    title: "Industries",
    links: [
      { label: "Banking", to: "/industries/banking" },
      { label: "Healthcare", to: "/industries/healthcare" },
      { label: "Retail", to: "/industries/retail" },
      { label: "Telecom", to: "/industries/telecom" },
      { label: "Insurance", to: "/industries/insurance" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Insights", to: "/insights" },
      { label: "Case Studies", to: "/#case-studies" },
      { label: "Technology", to: "/#technology" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms", to: "/terms" },
      { label: "Cookie Policy", to: "/cookie-policy" },
    ],
  },
];

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", testid: "social-linkedin" },
  { icon: XLogo, label: "X", testid: "social-twitter", url: "https://x.com/ClarityPointLLC" },
  { icon: Youtube, label: "YouTube", testid: "social-youtube" },
];

export default function Footer() {
  return (
    <footer className="relative border-t border-cyan-500/10 bg-slate-50" data-testid="site-footer">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Logo compact />
            <p className="mt-5 text-sm text-slate-600 leading-relaxed">
              Engineering What&apos;s Next. Technology, AI and digital engineering for ambitious enterprises.
            </p>
            <div className="mt-5 space-y-2 text-xs text-slate-500">
              <a href="mailto:hr@claritypointconsulting.org" data-testid="footer-email-link" className="block hover:text-cyan-600 transition-colors">hr@claritypointconsulting.org</a>
              <a href="tel:+12272656803" data-testid="footer-phone-link" className="block hover:text-cyan-600 transition-colors">+1 (227) 265-6803</a>
              <p>4550 Montgomery Ave Ste 301N, Bethesda, MD 20814</p>
            </div>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.url || "#"}
                  target={s.url ? "_blank" : undefined}
                  rel={s.url ? "noopener noreferrer" : undefined}
                  aria-label={s.label}
                  data-testid={s.testid}
                  className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 transition-all duration-300 hover:text-cyan-600 hover:border-cyan-500/50 hover:shadow-[0_0_16px_rgba(6,182,212,0.25)]"
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="font-mono-tech text-[0.65rem] tracking-[0.3em] uppercase text-cyan-600/80 mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-sm text-slate-600 hover:text-slate-900 transition-colors duration-200"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-14 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">© 2026 ClarityPoint Consulting. All rights reserved.</p>
          <p className="text-xs font-mono-tech text-slate-500 tracking-widest">IDEA → ARCHITECTURE → ENGINEERING → AI → SCALE</p>
        </div>
      </div>
    </footer>
  );
}
